import { createSelector } from 'reselect';

import defaultSettings from 'wix-vod-shared/src/app-settings/default-settings';
import { COMPACT_SHOW_VIDEO_LIST_VALUES } from 'wix-vod-shared/src/app-settings/constants/compact-show-video-list';
import { COMPACT_PLAY_TYPE_VALUES } from 'wix-vod-shared/src/app-settings/constants/compact-play-type';
import { MAIN_ACTION_STYLE_TYPES } from 'wix-vod-shared/src/app-settings/constants/main-action-style-types';
import { MAIN_ITEM_TYPE_VALUES } from 'wix-vod-shared/src/app-settings/constants/main-item-type-values';
import { LAYOUT_STYLE_VALUES } from 'wix-vod-shared/src/app-settings/constants/layout-style-values';
import { CHANNEL_LAYOUT_VALUES } from 'wix-vod-shared/src/app-settings/constants/channel-layout-values';
import { SHOW_INFO_VALUES } from 'wix-vod-shared/src/app-settings/constants/show-info-values';


export function getAllSettings(state) {
  return state.appSettings;
}

export function getEditMode(state) {
  return _.get(state, 'appSettings.editMode');
}

export function getRequestedWidgetSize(state) {
  return _.get(state, 'appSettings.requestedWidgetSize', {});
}

export function getIsInitialWidgetSizeWasSet(state) {
  return Boolean(getRequestedWidgetSize(state).width);
}

export function getNumberOfRows(state) {
  return _.get(state, 'appSettings.numbers.numberOfRows', defaultSettings.numbers.numberOfRows);
}

export function getVideosInRowCount(state) {
  return _.get(state, 'appSettings.numbers.videosInRow', defaultSettings.numbers.videosInRow);
}

export function getVideoInfoPosition(state) {
  return _.get(state, 'appSettings.numbers.videoInfoPosition', defaultSettings.numbers.videoInfoPosition);
}

export function getInfoActionVisibility(state) {
  return _.get(state, 'appSettings.numbers.showInfoAction', defaultSettings.numbers.showInfoAction);
}

export function getLayoutStyle(state) {
  return _.get(state, 'appSettings.numbers.layoutStyle', defaultSettings.numbers.layoutStyle);
}

export function getSiteUrl(state) {
  return _.get(state, 'appSettings.siteUrl');
}

export function getCompId(state) {
  return _.get(state, 'appSettings.compId');
}

export function getInstance(state) {
  return _.get(state, 'appSettings.instance');
}

export function getPlayType(state) {
  return _.get(state, 'appSettings.numbers.playType', defaultSettings.numbers.playType);
}

export function getCompactMainActionStyleType(state) {
  return _.get(state, 'appSettings.numbers.mainActionStyleType', defaultSettings.numbers.mainActionStyleType);
}

export function getChannelLayout(state) {
  return _.get(state, 'appSettings.numbers.channelLayout', defaultSettings.numbers.channelLayout);
}

export function getMainItemOverlayOpacity(state) {
  return _.get(state, 'appSettings.numbers.mainItemOverlayOpacity', defaultSettings.numbers.mainItemOverlayOpacity);
}

export function getVideoListVisibility(state) {
  return _.get(state, 'appSettings.numbers.videoListVisibility', defaultSettings.numbers.videoListVisibility);
}

export function getCompactShowVideoListType(state) {
  return _.get(state, 'appSettings.numbers.compactShowVideoListType', defaultSettings.numbers.compactShowVideoListType);
}

function getShowSearch(state) {
  return _.get(state, 'appSettings.booleans.showSearch', defaultSettings.booleans.showSearch);
}

export const isCompactLayout = createSelector(
  getChannelLayout,
  channelLayout => channelLayout === CHANNEL_LAYOUT_VALUES.COMPACT
);

export const isStripLayout = createSelector(
  getChannelLayout,
  channelLayout => channelLayout === CHANNEL_LAYOUT_VALUES.STRIP
);

export const isClassicLayout = createSelector(
  getChannelLayout,
  channelLayout => channelLayout === CHANNEL_LAYOUT_VALUES.CLASSIC
);

export const canShowSearch = createSelector(
  getShowSearch,
  isCompactLayout,
  (isSearchVisible, isCompact) => isSearchVisible && !isCompact
);

export function canShowMainItemTitle(state) {
  return _.get(state, 'appSettings.booleans.showTitleForMainItem', defaultSettings.booleans.showTitleForMainItem);
}

export function canShowActionBar(state) {
  return _.get(state, 'appSettings.booleans.showActionBar', defaultSettings.booleans.showActionBar);
}

function getCanShowChannelInfo(state) {
  return _.get(state, 'appSettings.booleans.showChannelInfo', defaultSettings.booleans.showChannelInfo);
}

export const canShowChannelInfo = createSelector(
  isCompactLayout,
  canShowActionBar,
  getCanShowChannelInfo,
  (isCompact, isActionBarVisible, isChannelInfoVisible) => {
    if (isCompact) {
      return isActionBarVisible && isChannelInfoVisible;
    }
    return isChannelInfoVisible;
  }
);

function getCanShowChannelShare(state) {
  return _.get(state, 'appSettings.booleans.showChannelShare', defaultSettings.booleans.showChannelShare);
}

export const canShowChannelShare = createSelector(
  isCompactLayout,
  canShowActionBar,
  getCanShowChannelShare,
  (isCompact, isActionBarVisible, isChannelShareVisible) => {
    if (isCompact) {
      return isActionBarVisible && isChannelShareVisible;
    }
    return isChannelShareVisible;
  }
);

function getCanShowSignIn(state) {
  return _.get(state, 'appSettings.booleans.showSignIn', defaultSettings.booleans.showSignIn);
}

export const canShowSignIn = createSelector(
  isCompactLayout,
  canShowActionBar,
  getCanShowSignIn,
  (isCompact, isActionBarVisible, isSignInVisible) => {
    if (isCompact) {
      return isActionBarVisible && isSignInVisible;
    }
    return isSignInVisible;
  }
);

export const canShowVideoList = createSelector(
  getCompactShowVideoListType,
  compactShowVideoListType => compactShowVideoListType !== COMPACT_SHOW_VIDEO_LIST_VALUES.NEVER_SHOW
);

function getMainItemPublisherVisibility(state) {
  return _.get(state, 'appSettings.booleans.showPublisherForMainItem', defaultSettings.booleans.showPublisherForMainItem);
}

export const canShowMainItemPublisher = createSelector(
  getMainItemPublisherVisibility,
  isCompactLayout,
  (isPublisherVisible, isCompact) => isPublisherVisible && !isCompact
);

export function isPreviewHover(state) {
  return _.get(state, 'appSettings.booleans.previewHover', false);
}

export function isVideoListPreviewHover(state) {
  return _.get(state, 'appSettings.booleans.videoListPreviewHover', false);
}

export function getVideoListPublisherVisibility(state) {
  return _.get(state, 'appSettings.booleans.showVideoPublisherInList', defaultSettings.booleans.showVideoPublisherInList);
}

export const canShowVideoListItemPublisher = createSelector(
  getVideoListPublisherVisibility,
  isCompactLayout,
  (isPublisherVisible, isCompact) => isPublisherVisible && !isCompact
);

export function getChannelIdFromAppSettings(state) {
  return _.get(state, 'appSettings.fonts.channelId');
}

export function canShowVideoListItemTitle(state) {
  return _.get(state, 'appSettings.booleans.showVideoTitleInList', defaultSettings.booleans.showVideoTitleInList);
}

export function canShowFullInfo(state) {
  return _.get(state, 'appSettings.booleans.showFullInfo', defaultSettings.booleans.showFullInfo);
}

export function canShareVideo(state) {
  return _.get(state, 'appSettings.booleans.enableVideosSharing', defaultSettings.booleans.enableVideosSharing);
}

export const canShowChannelTags = createSelector(
  [
    canShowSearch,
    state => _.get(state, 'appSettings.booleans.showChannelTags', defaultSettings.booleans.showChannelTags),
  ],
  (canShowSearch, canShowTags) => canShowSearch && canShowTags,
);

export function getMainItemType(state) {
  return _.get(state, 'appSettings.numbers.mainItemType', defaultSettings.numbers.mainItemType);
}

export const isMainItemFeaturedVideo = createSelector(
  [
    getMainItemType,
  ],
  mainItemType => mainItemType === MAIN_ITEM_TYPE_VALUES.FEATURED_VIDEO,
);

export const areElementsRounded = createSelector(
  [
    getLayoutStyle,
  ],
  layoutStyle => layoutStyle === LAYOUT_STYLE_VALUES.ROUND,
);

export const isCompactPlayInFrame = createSelector(
  [
    getPlayType,
  ],
  playType => playType === COMPACT_PLAY_TYPE_VALUES.IN_FRAME,
);

export const isCompactMainActionsOnlyIcons = createSelector(
  [
    getCompactMainActionStyleType,
  ],
  styleType => styleType === MAIN_ACTION_STYLE_TYPES.ICON,
);

export const isVideosListInfoAlwaysShown = createSelector(
  [
    getInfoActionVisibility,
  ],
  visibilityType => visibilityType === SHOW_INFO_VALUES.ALWAYS,
);

export const isVideosListInfoNeverShown = createSelector(
  [
    getInfoActionVisibility,
  ],
  visibilityType => visibilityType === SHOW_INFO_VALUES.NEVER,
);

export const isVideosListInfoShownOnHover = createSelector(
  [
    getInfoActionVisibility,
  ],
  visibilityType => visibilityType === SHOW_INFO_VALUES.ON_MOUSE_OVER,
);



// WEBPACK FOOTER //
// ./src/shared/selectors/app-settings.js